const PACKMYCART_HOST_PATTERN = /pack-my-cart(-dev)?\.web\.app/i;

export function safeDecodeUriComponent(value: string): string {
  let decoded = value;
  for (let i = 0; i < 4; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
}

export function fixMalformedProtocol(url: string): string {
  return url.replace(/^(https?):\/([^/])/i, '$1://$2');
}

function isPackMyCartHost(hostname: string): boolean {
  return PACKMYCART_HOST_PATTERN.test(hostname);
}

/** Single-slash app paths only; rejects protocol-relative `//host` open redirects. */
export function isSafeRelativePath(path: string): boolean {
  return /^\/[^/]/.test(path) && !/[{%]/.test(path);
}

export function isSafeExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function toInternalPath(url: string, siteOrigin = ''): string | null {
  const fixed = fixMalformedProtocol(url);

  const tryParse = (candidate: string): string | null => {
    try {
      const parsed = new URL(candidate);
      const origin = siteOrigin.replace(/\/$/, '');

      if ((origin && parsed.origin === origin) || isPackMyCartHost(parsed.hostname)) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return null;
    }
    return null;
  };

  return tryParse(fixed) || tryParse(`https://${fixed.replace(/^https?:\/\/?/i, '')}`);
}

/**
 * Repair malformed chat markdown hrefs (brace wrappers, double-encoding, nested URLs).
 */
export function normalizeChatLink(href: string, siteOrigin = ''): string {
  const origin = siteOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  let normalized = safeDecodeUriComponent(href.trim());

  normalized = normalized.replace(/^[{\s]+/, '').replace(/[}\s]+$/, '');

  const braceWrappedUrl = normalized.match(/^\{?\s*(https?:\/\/?[^\s}]+)\s*\}?$/i);
  if (braceWrappedUrl) {
    normalized = fixMalformedProtocol(braceWrappedUrl[1]);
  }

  const embeddedInPath = normalized.match(/^\/+\{?\s*(https?:\/\/?[^\s}]+)\s*\}?$/i);
  if (embeddedInPath) {
    normalized = fixMalformedProtocol(embeddedInPath[1]);
  }

  if (origin) {
    const escapedOrigin = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const nested = normalized.match(new RegExp(`^${escapedOrigin}/\\{?\\s*(https?:\\/\\/?[^\\s}]+)\\s*\\}?`, 'i'));
    if (nested) {
      normalized = fixMalformedProtocol(nested[1]);
    }
  }

  normalized = fixMalformedProtocol(normalized);

  const internalPath = toInternalPath(normalized, origin);
  if (internalPath) {
    return internalPath;
  }

  if (isSafeRelativePath(normalized)) {
    return normalized;
  }

  if (isSafeExternalUrl(normalized)) {
    return normalized;
  }

  return '#';
}

const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Normalize markdown links in a model reply before display or return.
 */
export function sanitizeBlackaReply(reply: string, siteOrigin = ''): string {
  return reply.replace(MARKDOWN_LINK, (match, label: string, href: string) => {
    const normalized = normalizeChatLink(href, siteOrigin);
    return `[${label}](${normalized})`;
  });
}
